import type { CSSProperties, ReactNode } from 'react';
import type { FormPathType, FormStatus } from '@engage/types';
import { FORM_UI_COLORS as C, FORM_UI_FONT } from './tokens';

/**
 * Shared atoms for the Forms UI — the building blocks every screen reuses.
 * Inline-styled to be self-contained (the engage frontend doesn't have a
 * heavy design system reach into this surface yet). All sized + coloured
 * to match the JT Engage forms mockup.
 */

// ─── PathChip — Candidate vs Contact ─────────────────────────────────────────

export function PathChip({
  type,
  size = 'sm',
}: {
  type: FormPathType;
  size?: 'sm' | 'lg';
}) {
  const isCand = type === 'CANDIDATE';
  const pad = size === 'sm' ? '2px 9px' : '4px 12px';
  const fs = size === 'sm' ? 11 : 12.5;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: pad,
        borderRadius: 999,
        fontSize: fs,
        fontWeight: 600,
        fontFamily: FORM_UI_FONT,
        color: isCand ? C.candidate : C.contact,
        background: isCand ? C.candidateBg : C.contactBg,
        whiteSpace: 'nowrap',
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: 2,
          background: isCand ? C.candidate : C.contact,
        }}
      />
      {isCand ? 'Candidate' : 'Contact'}
    </span>
  );
}

// ─── StatusChip — Draft / Published / Archived ───────────────────────────────

const STATUS_LOOKS: Record<FormStatus | 'REPUBLISHED', { c: string; b: string; dot: string }> = {
  DRAFT: { c: C.warn, b: C.warnBg, dot: C.warnDot },
  PUBLISHED: { c: C.tealDark, b: C.contactBg, dot: C.teal },
  REPUBLISHED: { c: C.tealDark, b: C.contactBg, dot: C.teal },
  ARCHIVED: { c: C.archived, b: C.archivedBg, dot: C.faint },
};
const STATUS_LABELS: Record<FormStatus, string> = {
  DRAFT: 'Draft',
  PUBLISHED: 'Published',
  ARCHIVED: 'Archived',
};

export function StatusChip({ status }: { status: FormStatus }) {
  const s = STATUS_LOOKS[status];
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '3px 10px',
        borderRadius: 999,
        fontSize: 11.5,
        fontWeight: 600,
        fontFamily: FORM_UI_FONT,
        color: s.c,
        background: s.b,
      }}
    >
      <span style={{ width: 6, height: 6, borderRadius: 99, background: s.dot }} />
      {STATUS_LABELS[status]}
    </span>
  );
}

// ─── Button ──────────────────────────────────────────────────────────────────

type BtnKind = 'primary' | 'teal' | 'ghost' | 'subtle' | 'danger';

export function Btn({
  children,
  kind = 'ghost',
  onClick,
  style,
  disabled,
  type = 'button',
}: {
  children: ReactNode;
  kind?: BtnKind;
  onClick?: () => void;
  style?: CSSProperties;
  disabled?: boolean;
  type?: 'button' | 'submit';
}) {
  const base: CSSProperties = {
    fontFamily: FORM_UI_FONT,
    fontWeight: 600,
    fontSize: 13,
    padding: '9px 16px',
    borderRadius: 9,
    cursor: disabled ? 'not-allowed' : 'pointer',
    border: '1px solid transparent',
    transition: 'all .15s',
    opacity: disabled ? 0.5 : 1,
    whiteSpace: 'nowrap',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 7,
  };
  const variants: Record<BtnKind, CSSProperties> = {
    primary: { background: C.indigo, color: '#fff' },
    teal: { background: C.teal, color: '#083123' },
    ghost: { background: '#fff', color: C.ink, border: `1px solid ${C.line}` },
    subtle: { background: C.bg, color: C.indigoSoft, border: `1px solid ${C.line}` },
    danger: { background: '#fff', color: C.danger, border: `1px solid ${C.dangerBg}` },
  };
  return (
    <button
      type={type}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      style={{ ...base, ...variants[kind], ...style }}
    >
      {children}
    </button>
  );
}

// ─── Icon ─────────────────────────────────────────────────────────────────────

export function Icon({
  d,
  size = 18,
  color = 'currentColor',
  fill = 'none',
  sw = 1.7,
}: {
  d: string | readonly string[];
  size?: number;
  color?: string;
  fill?: string;
  sw?: number;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={fill}
      stroke={color}
      strokeWidth={sw}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {typeof d === 'string' ? <path d={d} /> : d.map((p, i) => <path key={i} d={p} />)}
    </svg>
  );
}

export const ICONS = {
  forms: 'M9 2h6a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2zM9 7h6M9 11h6M9 15h3',
  search: ['M11 11m-7 0a7 7 0 1 0 14 0a7 7 0 1 0-14 0', 'M21 21l-4.3-4.3'],
  plus: ['M12 5v14', 'M5 12h14'],
  dots: ['M12 5h.01', 'M12 12h.01', 'M12 19h.01'],
  chev: 'M9 6l6 6-6 6',
  check: 'M5 12l5 5L20 7',
  x: ['M6 6l12 12', 'M18 6L6 18'],
  eye: ['M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z', 'M12 12m-3 0a3 3 0 1 0 6 0a3 3 0 1 0-6 0'],
  grip: ['M9 6h.01', 'M9 12h.01', 'M9 18h.01', 'M15 6h.01', 'M15 12h.01', 'M15 18h.01'],
  bolt: 'M13 2L4.5 13H11l-1 9 8.5-11H12l1-9z',
  shield: 'M12 3l8 3v5c0 5-3.5 8-8 10-4.5-2-8-5-8-10V6l8-3z',
  doc: ['M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z', 'M14 2v6h6'],
  layers: ['M12 2L2 7l10 5 10-5-10-5z', 'M2 17l10 5 10-5', 'M2 12l10 5 10-5'],
} as const;

// ─── Form input atoms ────────────────────────────────────────────────────────

export const INPUT_STYLE: CSSProperties = {
  width: '100%',
  padding: '9px 11px',
  border: `1px solid ${C.line}`,
  borderRadius: 8,
  fontFamily: FORM_UI_FONT,
  fontSize: 13,
  color: C.ink,
  boxSizing: 'border-box',
  background: '#fff',
};

export function Row({
  label,
  children,
  hint,
}: {
  label: string;
  children: ReactNode;
  hint?: string;
}) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label
        style={{
          fontSize: 11.5,
          fontWeight: 700,
          color: C.indigoSoft,
          display: 'block',
          marginBottom: 6,
          letterSpacing: 0.3,
        }}
      >
        {label}
      </label>
      {children}
      {hint && (
        <div style={{ fontSize: 10.8, color: C.faint, marginTop: 5, lineHeight: 1.45 }}>{hint}</div>
      )}
    </div>
  );
}

export function Toggle({
  on,
  label,
  sub,
  onChange,
}: {
  on: boolean;
  label: string;
  sub?: string;
  onChange?: (v: boolean) => void;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 12,
        padding: '10px 0',
        borderBottom: `1px solid ${C.line}`,
      }}
    >
      <div>
        <div style={{ fontSize: 12.5, fontWeight: 600, color: C.ink }}>{label}</div>
        {sub && (
          <div style={{ fontSize: 10.8, color: C.faint, marginTop: 2, lineHeight: 1.4 }}>{sub}</div>
        )}
      </div>
      <button
        type="button"
        onClick={() => onChange?.(!on)}
        aria-pressed={on}
        style={{
          width: 38,
          height: 22,
          borderRadius: 99,
          flexShrink: 0,
          background: on ? C.teal : '#D5D8E0',
          position: 'relative',
          cursor: 'pointer',
          border: 'none',
          padding: 0,
        }}
      >
        <span
          style={{
            position: 'absolute',
            top: 2,
            left: on ? 18 : 2,
            width: 18,
            height: 18,
            borderRadius: 99,
            background: '#fff',
            transition: 'left .15s',
            boxShadow: '0 1px 3px rgba(0,0,0,.2)',
          }}
        />
      </button>
    </div>
  );
}

export function Section({
  title,
  note,
  children,
}: {
  title: string;
  note?: string;
  children: ReactNode;
}) {
  return (
    <div style={{ marginBottom: 22 }}>
      <div
        style={{
          fontSize: 11.5,
          fontWeight: 800,
          color: C.indigo,
          letterSpacing: 0.4,
          marginBottom: note ? 4 : 12,
        }}
      >
        {title}
      </div>
      {note && (
        <div style={{ fontSize: 10.8, color: C.faint, lineHeight: 1.45, marginBottom: 12 }}>
          {note}
        </div>
      )}
      {children}
    </div>
  );
}

export function Pill({ children }: { children: ReactNode }) {
  return (
    <span
      style={{
        background: '#fff',
        border: `1px solid ${C.line}`,
        borderRadius: 7,
        padding: '3px 8px',
        fontWeight: 600,
        color: C.indigo,
        fontSize: 11.5,
        display: 'inline-block',
        margin: '2px 1px',
      }}
    >
      {children}
    </span>
  );
}
